/**
 * Authorization Enforcement
 * 
 * Wrapper functions for enforcing authorization in mutation paths.
 * These functions:
 * - Call the policy evaluator
 * - Throw AuthorizationError if denied
 * - Log decisions to the audit store
 * 
 * Use these in store actions to ensure consistent enforcement.
 */

import type {
  ActorContext,
  ResourceContext,
  EvaluationContext,
  PolicyDecision,
  ResourceType,
  CapabilityChecker,
} from './types';
import { AuthorizationError } from './types';
import {
  evaluateAuthorization,
  __evaluateAuthorizationCoreForTesting,
  explainDecision,
} from './policyEvaluator';
import { POLICY_RULES } from './policyRules';
import {
  buildAuthoredPolicyRules,
  getAuthoredPolicyCache,
} from './authoredPolicies';
import {
  evaluateAuthorizationV2,
  isGovernanceAction,
  isSessionAction,
  isCandidateModificationAction,
  isProposalConfigurationAction,
  type AuthorizationV1Delegates,
} from './policyEvaluatorV2';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useAuditStore } from '../../stores/prioritization/auditStore';
import { getCapabilitiesForAction } from '../../types/prioritization/capabilityIds';
import type { PermissionAction } from '../../types/prioritization/permissions';

// ============================================================================
// Context Builders
// ============================================================================

/** Normalize role name so DB labels (e.g. "Workspace Administrator", "workspace_admin") match canonical slugs. */
function normalizedRoleSlug(name: string): string {
  const n = name.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
  if (n === 'workspace-admin' || n === 'workspace-administrator') return 'workspace-admin';
  if (n === 'tenant-admin' || n === 'tenant-administrator') return 'tenant-admin';
  if (n === 'platform-admin' || n === 'platform-administrator' || n === 'system-admin') return 'platform-admin';
  return n;
}

/** True if assignedRoleNames contains any variant that means workspace-admin. */
export function hasWorkspaceAdminRole(assignedRoleNames: string[]): boolean {
  return assignedRoleNames.some((n) => normalizedRoleSlug(n) === 'workspace-admin');
}
/** True if assignedRoleNames contains any variant that means tenant-admin. */
export function hasTenantAdminRole(assignedRoleNames: string[]): boolean {
  return assignedRoleNames.some((n) => normalizedRoleSlug(n) === 'tenant-admin');
}
/** True if assignedRoleNames contains any variant that means platform-admin. */
export function hasPlatformAdminRole(assignedRoleNames: string[]): boolean {
  return assignedRoleNames.some((n) => normalizedRoleSlug(n) === 'platform-admin');
}

/**
 * Build actor context from the current user state.
 * roleIds includes canonical admin slugs (workspace-admin, tenant-admin, platform-admin) when the user has those roles (by any label).
 * Admin flags are derived from assignedRoleNames (normalized) so platform/tenant/workspace admins get full access within their scope.
 */

/**
 * Fields the policy layer and the audit trail read off an actor and a resource
 * that `ActorContext` / `ResourceContext` do not declare.
 *
 * These were reached through `as any` at 37 call sites, which is the same
 * assertion made 37 times with nothing checking that the names agree. Declared
 * once, a typo in one of them is a compile error instead of a silent `undefined`
 * written into an authorization audit row.
 */
interface ActorExtras {
  currentRole: string | null;
  workspaceRole: string | null;
  tenantRole: string | null;
  isPlatformAdmin: boolean;
  isTenantAdmin: boolean;
  isWorkspaceAdmin: boolean;
  currentWorkspaceId?: string;
  currentTenantId?: string;
  capabilityIds?: string[];
}

interface ResourceExtras {
  id?: string | null;
  ownerId?: string | null;
  createdBy?: string | null;
  createdByUserId?: string | null;
  created_by_user_id?: string | null;
  assigneeId?: string | null;
  workspaceId?: string | null;
  workspace_id?: string | null;
  lifecycleState?: string | null;
}

/** An actor as this module writes and reads it. */
type ActorWithExtras = ActorContext & Partial<ActorExtras>;
/** A resource as the audit trail reads it. */
type ResourceWithExtras = { readonly [K in keyof ResourceExtras]?: ResourceExtras[K] };


export function buildActorContext(): ActorContext {
  const userStore = useUserStore.getState();
  const userId = userStore.userId;
  const currentRole = userStore.currentRole;
  const assignedRoleNames = userStore.assignedRoleNames ?? [];

  const tenantStore = useTenantStore.getState();
  const currentWorkspaceId = tenantStore.currentWorkspaceId ?? getCurrentWorkspaceId();
  const currentTenantId = tenantStore.currentTenantId;
  const workspaceRole = currentWorkspaceId
    ? tenantStore.getUserWorkspaceRole(userId, currentWorkspaceId)
    : null;
  const tenantRole = currentTenantId
    ? tenantStore.getUserTenantRole(userId, currentTenantId)
    : null;

  const isWorkspaceAdmin = hasWorkspaceAdminRole(assignedRoleNames);
  const isTenantAdmin = hasTenantAdminRole(assignedRoleNames);
  const isPlatformAdmin = hasPlatformAdminRole(assignedRoleNames);

  // Every role the person actually holds, not just the one the UI happens to
  // have selected. This list was previously currentRole + workspace/tenant/admin
  // roles only, so `hasAnyRole` saw a single arbitrary role: an Item Manager who
  // also holds Track Owner, Values Approver, Session Participant, Catchball
  // Contributor, Strategist and Workspace Member was evaluated as whichever one
  // currentRole happened to be, and the decision was logged as
  // actorRoleIds: ["workspace-member"]. assignedRoleNames was already loaded here
  // and read for the three admin checks below; it just never reached roleIds.
  const roleIds = [
    ...(currentRole ? [currentRole] : []),
    ...assignedRoleNames,
    ...(workspaceRole ? [workspaceRole] : []),
    ...(tenantRole ? [tenantRole] : []),
    ...(isWorkspaceAdmin ? ['workspace-admin'] : []),
    ...(isTenantAdmin ? ['tenant-admin'] : []),
    ...(isPlatformAdmin ? ['platform-admin'] : []),
  ].filter((r, i, a) => r != null && a.indexOf(r) === i);

  const base: ActorContext = {
    userId,
    roleIds,
    isAdmin: isPlatformAdmin,
    displayName: userStore.userName,
  };
  const withExtras = base as ActorWithExtras;
  withExtras.currentRole = currentRole ?? null;
  withExtras.workspaceRole = workspaceRole ?? null;
  withExtras.tenantRole = tenantRole ?? null;
  withExtras.isPlatformAdmin = isPlatformAdmin;
  withExtras.isTenantAdmin = isTenantAdmin;
  withExtras.isWorkspaceAdmin = isWorkspaceAdmin;
  withExtras.currentWorkspaceId = currentWorkspaceId ?? undefined;
  withExtras.currentTenantId = currentTenantId ?? undefined;
  const resolvedCapabilityIds = userStore.resolvedCapabilityIds ?? [];
  withExtras.capabilityIds = resolvedCapabilityIds.length > 0 ? resolvedCapabilityIds : undefined;
  return base;
}

/**
 * Build resource context from resource data.
 * Accepts both camelCase and snake_case; canonical output is camelCase for policy predicates.
 */
export function buildResourceContext(
  type: ResourceType,
  id: string,
  data: {
    createdBy?: string;
    createdByUserId?: string;
    created_by?: string;
    created_by_user_id?: string;
    assigneeId?: string;
    shapingOwnerId?: string;
    shaping_owner_id?: string;
    /** @deprecated DB column steward_id is unused; kept for backward compat only */
    stewardId?: string;
    steward_id?: string;
    ownerId?: string;
    owner_id?: string;
    lifecycleState?: string;
    lifecycle_state?: string;
    detailedState?: string;
    detailed_state?: string;
    isLocked?: boolean;
    lockState?: ResourceContext['lockState'];
    isPublished?: boolean;
    attributes?: Record<string, unknown>;
    scope?: ResourceContext['scope'];
    workspaceId?: string;
    workspace_id?: string;
    facilitatorId?: string;
    facilitator_id?: string;
  }
): ResourceContext {
  const workspaceId = data.workspaceId ?? data.workspace_id;
  const createdBy = data.createdBy ?? data.createdByUserId ?? data.created_by_user_id ?? data.created_by;
  const lifecycleState =
    data.lifecycleState ?? data.lifecycle_state ?? data.detailedState ?? data.detailed_state;
  const facilitatorTop = data.facilitatorId ?? data.facilitator_id;
  const mergedAttributes: Record<string, unknown> = { ...(data.attributes ?? {}) };
  if (facilitatorTop != null && facilitatorTop !== '') {
    mergedAttributes.facilitatorId = facilitatorTop;
  }
  const attributes =
    Object.keys(mergedAttributes).length > 0 ? mergedAttributes : data.attributes;
  return {
    type,
    id,
    createdBy,
    assigneeId: data.assigneeId ?? data.shapingOwnerId ?? (data as { shaping_owner_id?: string }).shaping_owner_id ?? data.stewardId ?? (data as { steward_id?: string }).steward_id,
    ownerId: data.ownerId ?? (data as { owner_id?: string }).owner_id,
    lifecycleState: lifecycleState ?? undefined,
    lockState: data.lockState ?? (data.isLocked ? { isLocked: true } : undefined),
    isPublished: data.isPublished,
    attributes,
    scope: data.scope,
    ...(workspaceId !== undefined && workspaceId !== null && { workspaceId }),
  };
}

/**
 * Build evaluation context from current app state.
 */
export function buildEvaluationContext(
  overrides?: Partial<EvaluationContext>
): EvaluationContext {
  // Get cycle/process context if available
  // This would typically come from a context provider or store
  
  return {
    now: new Date(),
    submissionWindowOpen: true, // Default to open; override as needed
    contributionsFrozen: false,
    ...overrides,
  };
}

/**
 * Log when legacy poolLocked disagrees with decomposed cyclePoolLocked/sessionPoolLocked.
 * Diagnostic only; no PII. Only logs on mismatch.
 * Set AUTHZ_POOL_LOCK_MISMATCH_LOG=0 to suppress.
 */
function logPoolLockMismatchIfNeeded(
  evalContext: EvaluationContext,
  action: string,
  resource: ResourceContext
): void {
  const p = evalContext.prerequisites;
  if (p == null) return;
  const poolLocked = p.poolLocked;
  if (typeof poolLocked !== 'boolean') return;
  const cyclePoolLocked = p.cyclePoolLocked;
  const sessionPoolLocked = p.sessionPoolLocked;
  const hasDecomposed = typeof cyclePoolLocked === 'boolean' || typeof sessionPoolLocked === 'boolean';
  if (!hasDecomposed) return;

  let expectedPoolLocked: boolean;
  if (typeof cyclePoolLocked === 'boolean' && typeof sessionPoolLocked === 'boolean') {
    expectedPoolLocked = cyclePoolLocked && sessionPoolLocked;
  } else if (typeof cyclePoolLocked === 'boolean') {
    expectedPoolLocked = cyclePoolLocked;
  } else {
    expectedPoolLocked = sessionPoolLocked as boolean;
  }

  if (poolLocked === expectedPoolLocked) return;
  if (typeof process !== 'undefined' && process.env?.AUTHZ_POOL_LOCK_MISMATCH_LOG === '0') return;

  const payload = {
    event: 'authz.pool_lock_mismatch' as const,
    reason: 'poolLocked_vs_decomposed' as const,
    actionKey: action,
    resourceType: resource.type,
    resourceId: resource.id,
    poolLocked,
    cyclePoolLocked,
    sessionPoolLocked,
    ...(typeof cyclePoolLocked === 'boolean' && typeof sessionPoolLocked === 'boolean' && { expected_poolLocked: expectedPoolLocked }),
  };
  console.warn('[AUTH]', payload);
}

/**
 * Build a capability checker using the current user's permissions.
 * Checks both the static role bundle (built-in admin roles) and the DB-resolved
 * capability IDs (custom roles), so users with granular DB permissions are recognized.
 */
export function buildCapabilityChecker(): CapabilityChecker {
  const userStore = useUserStore.getState();

  return (action: string, scope?) => {
    // Fast-path: static role map handles built-in admin roles
    if (userStore.can(action as PermissionAction, scope)) return true;

    // For custom roles: check DB-loaded capability IDs against the action→capability mapping
    const dbCaps = userStore.resolvedCapabilityIds ?? [];
    if (dbCaps.length > 0) {
      const required = getCapabilitiesForAction(action as PermissionAction);
      if (required.some((cap) => dbCaps.includes(cap))) return true;
    }

    return false;
  };
}

// ============================================================================
// Enforcement Functions
// ============================================================================

/**
 * Enforce authorization for an action.
 * Throws AuthorizationError if denied.
 * 
 * @param action - The action being attempted (e.g., 'items.edit.content')
 * @param resource - The resource being acted upon
 * @param context - Optional evaluation context overrides
 * @returns The policy decision (if allowed)
 * @throws AuthorizationError if denied
 */
/** V1 delegates for v2 (governance and session); uses core to avoid double shadow. */
function getGovernanceV1Delegates(): AuthorizationV1Delegates {
  return {
    evaluateAuthorizationV1: __evaluateAuthorizationCoreForTesting,
    evaluateAndExplainV1: (actor, action, resource, context, rules, capabilityChecker) => {
      const d = __evaluateAuthorizationCoreForTesting(actor, action, resource, context, rules, capabilityChecker);
      return { decision: d, explanation: explainDecision(d) };
    },
  };
}

/**
 * The built-in rules with the workspace's authored policies in front.
 *
 * Authored policies only deny (canon 06 §14, Layer 4b: "a policy cannot grant
 * an action"), and canon's summary table says policies can still block
 * platform, tenant and workspace admins — so they sit ahead of the three admin
 * overrides, matching the database check (migration 935), which does not
 * exempt admins either.
 */
export function getEffectivePolicyRules() {
  const authoredRules = buildAuthoredPolicyRules(getAuthoredPolicyCache());
  if (authoredRules.length === 0) return POLICY_RULES;
  return [...authoredRules, ...POLICY_RULES];
}

export function enforceAuthorization(
  action: string,
  resource: ResourceContext,
  context?: Partial<EvaluationContext>
): PolicyDecision {
  const actor = buildActorContext();
  const evalContext = buildEvaluationContext(context);
  logPoolLockMismatchIfNeeded(evalContext, action, resource);
  const capabilityChecker = buildCapabilityChecker();
  const effectiveRules = getEffectivePolicyRules();

  const useV2 =
    isGovernanceAction(action) ||
    isSessionAction(action) ||
    isCandidateModificationAction(action) ||
    isProposalConfigurationAction(action);
  const decision = useV2
    ? evaluateAuthorizationV2(
        {
          actor,
          action: { key: action },
          resource,
          context: evalContext,
          rules: effectiveRules,
          capabilityChecker,
        },
        getGovernanceV1Delegates()
      )
    : evaluateAuthorization(
        actor,
        { key: action },
        resource,
        evalContext,
        effectiveRules,
        capabilityChecker
      );

  if (import.meta.env.DEV) {
    console.log('[AUTH DEBUG]', JSON.stringify({
      usedV2: useV2,
      action: typeof action === 'string' ? action : (action as { key?: string })?.key,
      engine: (decision as { engine?: string }).engine ?? null,
      ruleId: decision.ruleId ?? null,
      reasons: decision.reasons ?? null,
      allowed: decision.allowed,
      actorUserId: actor.userId,
      actorRoleIds: actor.roleIds,
      actorCurrentWorkspaceId: (actor as ActorWithExtras).currentWorkspaceId ?? null,
      actorWorkspaceRole: (actor as ActorWithExtras).workspaceRole ?? null,
      actorTenantRole: (actor as ActorWithExtras).tenantRole ?? null,
      actorCurrentRole: (actor as ActorWithExtras).currentRole ?? null,
      actorIsPlatformAdmin: (actor as ActorWithExtras).isPlatformAdmin ?? null,
      actorIsTenantAdmin: (actor as ActorWithExtras).isTenantAdmin ?? null,
      actorIsWorkspaceAdmin: (actor as ActorWithExtras).isWorkspaceAdmin ?? null,
      resourceId: (resource as ResourceWithExtras).id ?? null,
      resourceOwnerId: (resource as ResourceWithExtras).ownerId ?? null,
      resourceCreatedBy: (resource as ResourceWithExtras).createdBy ?? null,
      resourceCreatedByUserId:
        (resource as ResourceWithExtras).createdByUserId ??
        (resource as ResourceWithExtras).created_by_user_id ??
        null,
      resourceAssigneeId: (resource as ResourceWithExtras).assigneeId ?? null,
      resourceWorkspaceId:
        (resource as ResourceWithExtras).workspaceId ??
        (resource as ResourceWithExtras).workspace_id ??
        null,
      resourceLifecycleState: (resource as ResourceWithExtras).lifecycleState ?? null,
    }, null, 2));
  }

  // Always log the decision (for denied actions, especially)
  if (!decision.allowed) {
    logAuthorizationDecision(decision, actor, action, resource);
    throw new AuthorizationError(decision, action, resource.type, resource.id);
  }
  
  // Optionally log allowed decisions for sensitive actions
  if (shouldLogAllowedAction(action)) {
    logAuthorizationDecision(decision, actor, action, resource);
  }
  
  return decision;
}

/**
 * Check authorization without throwing.
 * Returns the decision for UI gating.
 */
export function checkAuthorization(
  action: string,
  resource: ResourceContext,
  context?: Partial<EvaluationContext>
): PolicyDecision {
  const actor = buildActorContext();
  const evalContext = buildEvaluationContext(context);
  logPoolLockMismatchIfNeeded(evalContext, action, resource);
  const capabilityChecker = buildCapabilityChecker();
  const effectiveRules = getEffectivePolicyRules();

  const usedV2 =
    isGovernanceAction(action) ||
    isSessionAction(action) ||
    isCandidateModificationAction(action) ||
    isProposalConfigurationAction(action);
  let decision: PolicyDecision;
  if (usedV2) {
    decision = evaluateAuthorizationV2(
      {
        actor,
        action: { key: action },
        resource,
        context: evalContext,
        rules: effectiveRules,
        capabilityChecker,
      },
      getGovernanceV1Delegates()
    );
  } else {
    decision = evaluateAuthorization(
      actor,
      { key: action },
      resource,
      evalContext,
      effectiveRules,
      capabilityChecker
    );
  }

  if (import.meta.env.DEV) {
    console.log('[AUTH DEBUG]', JSON.stringify({
      usedV2,
      action: typeof action === 'string' ? action : (action as { key?: string })?.key,
      engine: (decision as { engine?: string }).engine ?? null,
      ruleId: decision.ruleId ?? null,
      reasons: decision.reasons ?? null,
      allowed: decision.allowed,
      actorUserId: actor.userId,
      actorRoleIds: actor.roleIds,
      actorWorkspaceRole: (actor as ActorWithExtras).workspaceRole ?? null,
      actorTenantRole: (actor as ActorWithExtras).tenantRole ?? null,
      actorCurrentRole: (actor as ActorWithExtras).currentRole ?? null,
      actorIsPlatformAdmin: (actor as ActorWithExtras).isPlatformAdmin ?? null,
      actorIsTenantAdmin: (actor as ActorWithExtras).isTenantAdmin ?? null,
      actorIsWorkspaceAdmin: (actor as ActorWithExtras).isWorkspaceAdmin ?? null,
      resourceOwnerId: (resource as ResourceWithExtras).ownerId ?? null,
      resourceCreatedByUserId:
        (resource as ResourceWithExtras).createdByUserId ??
        (resource as ResourceWithExtras).created_by_user_id ??
        null,
      resourceAssigneeId: (resource as ResourceWithExtras).assigneeId ?? null,
      resourceWorkspaceId:
        (resource as ResourceWithExtras).workspaceId ??
        (resource as ResourceWithExtras).workspace_id ??
        null,
    }, null, 2));
  }

  return decision;
}

/**
 * Check if an action is allowed (simple boolean).
 * Convenience function for quick checks.
 */
export function isActionAllowed(
  action: string,
  resource: ResourceContext,
  context?: Partial<EvaluationContext>
): boolean {
  return checkAuthorization(action, resource, context).allowed;
}

// ============================================================================
// Audit Logging
// ============================================================================

/**
 * Determine if an allowed action should be logged.
 * We log sensitive actions even when allowed.
 */
function shouldLogAllowedAction(action: string): boolean {
  const sensitiveActions = [
    'items.delete',
    'lists.publish',
    'process.publish',
    'items.edit',
    'lists.move_cutline',
    'admin.',
  ];
  
  return sensitiveActions.some(sensitive => 
    action === sensitive || action.startsWith(sensitive)
  );
}

/**
 * Log an authorization decision to the audit store.
 */
export function logAuthorizationDecision(
  decision: PolicyDecision,
  actor: ActorContext,
  action: string,
  resource: ResourceContext
): void {
  try {
    const auditStore = useAuditStore.getState();
    
    // Use the existing logEvent method
    auditStore.logEvent({
      type: 'policy_decision',
      actorId: actor.userId,
      actorRole: actor.roleIds[0] || 'unknown',
      actorName: actor.displayName,
      action,
      resourceType: resource.type,
      resourceId: resource.id,
      decision: decision.decision,
      ruleId: decision.ruleId,
      reasonCodes: decision.reasons.map(r => r.code),
      metadata: {
        capabilityPassed: decision.capabilityPassed,
        reasons: decision.reasons,
      },
    });
  } catch (error) {
    // Don't let audit logging failures break the app
    console.error('[Authorization] Failed to log decision:', error);
  }
}

// ============================================================================
// Convenience Enforcement Functions
// ============================================================================

/**
 * Enforce authorization for editing an item.
 */
export function enforceItemEdit(
  itemId: string,
  itemData: Record<string, unknown>,
  field?: string,
  context?: Partial<EvaluationContext>
): PolicyDecision {
  const action = field ? `items.edit.${field}` : 'items.edit';
  const resource = buildResourceContext('item', itemId, itemData);
  return enforceAuthorization(action, resource, context);
}

/**
 * Enforce authorization for deleting an item.
 */
export function enforceItemDelete(
  itemId: string,
  itemData: Record<string, unknown>,
  context?: Partial<EvaluationContext>
): PolicyDecision {
  const resource = buildResourceContext('item', itemId, itemData);
  return enforceAuthorization('items.delete', resource, context);
}

/**
 * Enforce authorization for publishing a list.
 */
export function enforceListPublish(
  listId: string,
  listData: Record<string, unknown>,
  context?: Partial<EvaluationContext>
): PolicyDecision {
  const resource = buildResourceContext('list', listId, listData);
  return enforceAuthorization('lists.publish', resource, context);
}

/**
 * Enforce authorization for shaping an item.
 */
export function enforceItemShape(
  itemId: string,
  itemData: Record<string, unknown>,
  context?: Partial<EvaluationContext>
): PolicyDecision {
  const resource = buildResourceContext('item', itemId, itemData);
  return enforceAuthorization('items.edit', resource, context);
}

// ============================================================================
// Error Recovery
// ============================================================================

/**
 * Check if an error is an AuthorizationError.
 */
export function isAuthorizationError(error: unknown): error is AuthorizationError {
  return error instanceof AuthorizationError;
}

/**
 * Extract a user-friendly message from an AuthorizationError.
 */
export function getAuthorizationErrorMessage(error: AuthorizationError): string {
  const primaryReason = error.decision.reasons[0];
  
  switch (primaryReason?.code) {
    case 'MISSING_CAPABILITY':
      return "You don't have permission to perform this action.";
    case 'RESOURCE_LOCKED':
      return 'This item is locked and cannot be modified.';
    case 'ALREADY_PUBLISHED':
      return 'This item has been published and cannot be modified.';
    case 'NOT_OWNER':
      return 'Only the owner can perform this action.';
    case 'SUBMISSION_WINDOW_CLOSED':
      return 'The submission window is closed.';
    default:
      return 'You are not authorized to perform this action.';
  }
}
